import {
  PDFDocument,
  StandardFonts,
  PDFCheckBox,
  PDFTextField,
  PDFName,
  PDFDict,
  PDFNumber,
} from 'pdf-lib';
import { filingDebtors } from '../../utils/debtors';
import {
  amendmentServiceDescription,
  amendmentServiceRecipients,
  appendAmendmentServiceList,
} from '../../utils/creditorMailingMatrix';
const API_URL2 = process.env.REACT_APP_BACKEND_URL
const API_URL = process.env.REACT_APP_API_URL;
const token = localStorage.getItem("accessToken");

// --- Date helpers for amended docs ---
// Return local-today as YYYY-MM-DD (no timezone surprises)
function isoToday() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
// If amended, use today's date; otherwise use provided execution_date
function resolveExecDate(data) {
  return (data && data.amended) ? isoToday() : (data?.execution_date || '');
}

async function finishGeneratedPdf(blob, filename, { download = true } = {}) {
  let finalBlob = blob;
  const formData = new FormData();
  formData.append('file', blob, 'draft.pdf');
  try {
    const response = await fetch(`${API_URL2}/optimize-pdf/`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (response.ok) finalBlob = await response.blob();
  } catch (error) {
    console.error('PDF optimization failed; using the generated PDF.', error);
  }

  if (download) {
    const objectUrl = URL.createObjectURL(finalBlob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(objectUrl);
  }
  return finalBlob;
}
async function generateEnvelopesPdf(creditors) {
    if (!creditors || creditors.length === 0) return;
    const envDoc = await PDFDocument.create();
    const seen = new Set();
    creditors = creditors.filter(c => {
      const key = [c.name, c.street1, c.street2 || '', c.city, c.state, c.zip_code]
        .join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    // 1. Load & embed your logo (adjust the path as needed)
    const logoUrl = '/images/logo.png';
    const logoBytes = await fetch(logoUrl).then(r => r.arrayBuffer());
    const logoImage = await envDoc.embedPng(logoBytes);
    const logoDims = logoImage.scale(0.1);
  
    // 2. Prepare font and layout constants
    const font = await envDoc.embedFont(StandardFonts.Helvetica);
    const fontSize = 10;
    const lineHeight = fontSize + 4;
    const envelopeSize = [9.5 * 72, 4.125 * 72]; // width × height in points
    const margin = 25;
  
    // Your return address lines
    const returnAddress = [
      'Cisowski Law',
      '3119 Bristol Hwy Ste 217',
      'Johnson City, TN 37601',
    ];
  
    // 3. One page per creditor
    for (const c of creditors) {
      const page = envDoc.addPage(envelopeSize);
      const { width, height } = page.getSize();
  
      // 3a. Draw logo in top-left
      page.drawImage(logoImage, {
        x: margin,
        y: height - margin - logoDims.height,
        width: logoDims.width,
        height: logoDims.height,
      });
  
      // 3b. Draw return address to the right of the logo
      let yPos = height - margin - 9;
      for (const line of returnAddress) {
        page.drawText(line, {
          x: margin + logoDims.width + 10,
          y: yPos,
          size: fontSize,
          font,
        });
        yPos -= fontSize + 2;
      }
  
      // draw recipient address centered vertically and horizontally
      const addrLines = [
        c.name,
        `${c.street1}${c.street2?.trim() ? `, ${c.street2}` : ''}`,
        `${c.city}, ${c.state} ${c.zip_code}`,
      ];
      // measure max line width
      const lineWidths = addrLines.map(line => font.widthOfTextAtSize(line, fontSize));
      const maxWidth = Math.max(...lineWidths);
      const startX = (width - maxWidth) / 2;
      let startY = height / 2 + (addrLines.length / 2) * lineHeight;
      for (const line of addrLines) {
        page.drawText(line, {
          x: startX,
          y: startY,
          size: fontSize,
          font,
        });
        startY -= lineHeight;
      }
    }
  
    // 4. Save & trigger download
    const pdfBytes = await envDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'envelopes.pdf';
    link.click();
  }
// Create a letter‑sized PDF listing served creditors
async function generateCreditorsListPdf(creditors) {
    // Create the document
    const listDoc = await PDFDocument.create();
    creditors = Array.isArray(creditors) ? creditors : [];
  
    // Deduplicate by name & address
    const seen = new Set();
    creditors = creditors.filter(c => {
      const key = [c.name, c.street1, c.street2 || '', c.city, c.state, c.zip_code]
        .join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  
    const helvetica = await listDoc.embedFont(StandardFonts.Helvetica);
    const pageSize = [612, 792]; // Letter size
    let page = listDoc.addPage(pageSize);
    const { width, height } = page.getSize();
    const margin = 50;
    let y = height - margin;
    const lineHeight = 12;
    const indent = margin + 10;

    if (creditors.length === 0) {
      const message = 'No additional creditors were served.';
      const fontSize = 11;
      const textWidth = helvetica.widthOfTextAtSize(message, fontSize);
      page.drawText(message, {
        x: (width - textWidth) / 2,
        y: height - 100,
        size: fontSize,
        font: helvetica,
      });
      return listDoc;
    }
  
    for (const c of creditors) {
      // Name
      page.drawText(c.name, { x: margin, y, size: 10, font: helvetica });
      y -= lineHeight;
      // Address line 1
      page.drawText(
        `${c.street1}${c.street2?.trim() ? ', ' + c.street2 : ''}`,
        { x: indent, y, size: 10, font: helvetica }
      );
      y -= lineHeight;
      // City, State Zip
      page.drawText(
        `${c.city}, ${c.state} ${c.zip_code}`,
        { x: indent, y, size: 10, font: helvetica }
      );
      y -= lineHeight;
      // Method
      page.drawText(`Served via: ${c.method}`, { x: indent, y, size: 10, font: helvetica });
      y -= lineHeight * 1.5;
  
      if (y < margin) {
        page = listDoc.addPage(pageSize);
        y = height - margin;
      }
    }
  
    return listDoc;
  }

export async function fillFormBrowser(data = {}) {
    data = { ...data, debtors: filingDebtors(data.debtors) };
    console.log('fillFormBrowser called with:', { data});
  // provide defaults so we can safely reference properties
    // helper to format dates as MM/DD/YYYY
    const fmtDate = (s) => {
      if (!s) return '';
      const [year, month, day] = s.split('-');
      return `${month}/${day}/${year}`;
    };
    // helper to format dates as a long string, e.g. "Thursday, May 29, 2025"
    const fmtLongDate = (s) => {
      if (!s) return '';
      const [year, month, day] = s.split('-').map(Number);
      // Construct date in local timezone to avoid UTC offset issues
      const d = new Date(year, month - 1, day);
      return d.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    };
    const execDateISO = resolveExecDate(data);
    // load the blank PDF

    const url = '/forms/101-voluntary-petition.pdf';
    let amendedPdfDoc = null;
    const existingPdfBytes = await fetch(url).then(res => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const primaryDebtor = (data.debtors || []).find(d => d.is_primary) || {};
    const form = pdfDoc.getForm();

    //create the notice of amended if amended and check the amended box

    if (data.amended){
        form.getCheckBox('Check Box2').check();
        const amendedUrl = '/forms/notice-of-amendment.pdf'
        const amendedExistingPdfBytes = await fetch(amendedUrl).then(res => res.arrayBuffer());
        amendedPdfDoc = await PDFDocument.load(amendedExistingPdfBytes);
        const amendedForm = amendedPdfDoc.getForm();

        (data.debtors || []).forEach((debtor, index) => {
            const amendPrefix = debtor.is_primary ? 'debtor1' : 'debtor2';
            amendedForm.getTextField(`${amendPrefix}`).setText(`${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
        });
        amendedForm.getTextField('case_number').setText(data.case_number || '');
        amendedForm.getTextField('chapter').setText(data.chapter || '');
        amendedForm.getTextField('date').setText(fmtLongDate(execDateISO));
        amendedForm.getTextField('amended_files').setText("Amended Voluntary Petition");
        amendedForm.getTextField('items_served').setText("Amended Voluntary Petition and Notice of Amendment");
        amendedForm.getTextField('reason_amended').setText(data.amendedReason);
        amendedForm.getTextField('creditors_served_description').setText(
          amendmentServiceDescription(data),
        );
        const helvetica = await amendedPdfDoc.embedFont(StandardFonts.Helvetica);
        amendedForm.updateFieldAppearances(helvetica);
        amendedForm.flatten();
    }
  
  // text fields
  form.getTextField('Bankruptcy District Information').setText('Eastern District of Tennessee');
  form.getTextField('Case number1').setText(data.case_number || '');
  form.getTextField('Case number1').setText(data.case_number || '');
    
    if (data.debt_type === "consumer"){
        form.getCheckBox('Check Box18#1').check();
        form.getCheckBox('Check Box19#0').check();
    } else {
        form.getCheckBox('Check Box18#0').check();
        form.getCheckBox('Check Box19#1').check();
    }
    if (data.chapter === '7'){
        form.getCheckBox('Check Box20#1').check();
        form.getCheckBox('Check Box20A#0').check();
    } else {
        form.getCheckBox('Check Box20#0').check();
    }

    if (data.creditor_count <50){
        form.getCheckBox('Check Box21#0').check();
    } else if (data.creditor_count <100) {
        form.getCheckBox('Check Box21#1').check();
    } else if (data.creditor_count <200) {
        form.getCheckBox('Check Box21#2').check();
    } else if (data.creditor_count <1000) {
        form.getCheckBox('Check Box21#3').check();
    } else if (data.creditor_count <5001) {
        form.getCheckBox('Check Box21#4').check();
    } else if (data.creditor_count <10001) {
        form.getCheckBox('Check Box21#5').check();
    } else if (data.creditor_count <25001) {
        form.getCheckBox('Check Box21#6').check();
    } else if (data.creditor_count <50001) {
        form.getCheckBox('Check Box21#7').check();
    } else if (data.creditor_count <100001) {
        form.getCheckBox('Check Box21#8').check();
    } else {
        form.getCheckBox('Check Box21#9').check();
    }
    if (data.total_debt_amount <50001){
        form.getCheckBox('Check Box23#0').check();
    } else if (data.total_debt_amount <100001) {
        form.getCheckBox('Check Box23#1').check();
    } else if (data.total_debt_amount <500001) {
        form.getCheckBox('Check Box23#2').check();
    } else if (data.total_debt_amount <1000001) {
        form.getCheckBox('Check Box23#3').check();
    } else if (data.total_debt_amount <10000001) {
        form.getCheckBox('Check Box23#4').check();
    } else if (data.total_debt_amount <50000001) {
        form.getCheckBox('Check Box23#5').check();
    } else if (data.total_debt_amount <100000001) {
        form.getCheckBox('Check Box23#6').check();
    } else if (data.total_debt_amount <500000001) {
        form.getCheckBox('Check Box23#7').check();
    } else if (data.total_debt_amount <1000000001) {
        form.getCheckBox('Check Box23#8').check();
    } else if (data.total_debt_amount <10000000001) {
        form.getCheckBox('Check Box23#9').check();
    } else if (data.total_debt_amount <50000000001) {
        form.getCheckBox('Check Box23#10').check();
    } else {
        form.getCheckBox('Check Box23#11').check();
    }
    const total_asset_amount = data.real_property_total + data.personal_property_total
    if (total_asset_amount <50001){
        form.getCheckBox('Check Box22#0').check();
    } else if (total_asset_amount <100001) {
        form.getCheckBox('Check Box22#1').check();
    } else if (total_asset_amount <500001) {
        form.getCheckBox('Check Box22#2').check();
    } else if (total_asset_amount <1000001) {
        form.getCheckBox('Check Box22#3').check();
    } else if (total_asset_amount <10000001) {
        form.getCheckBox('Check Box22#4').check();
    } else if (total_asset_amount <50000001) {
        form.getCheckBox('Check Box22#5').check();
    } else if (total_asset_amount <100000001) {
        form.getCheckBox('Check Box22#6').check();
    } else if (total_asset_amount <500000001) {
        form.getCheckBox('Check Box22#7').check();
    } else if (total_asset_amount <1000000001) {
        form.getCheckBox('Check Box22#8').check();
    } else if (total_asset_amount <10000000001) {
        form.getCheckBox('Check Box22#9').check();
    } else if (total_asset_amount <50000000001) {
        form.getCheckBox('Check Box22#10').check();
    } else {
        form.getCheckBox('Check Box22#11').check();
    }
    
    // check chapter boxes
    const chapterBoxes = {
      '7': 'Check Box1#0',
      '11': 'Check Box1#1',
      '12': 'Check Box1#2',
      '13': 'Check Box1#3',
    };
    const boxName = chapterBoxes[data.chapter];
    if (boxName) {
      form.getCheckBox(boxName).check();
    }

    
    form.getCheckBox('Check Box15#0').check();
  (data.hazerdous_properties || []).forEach((pb,index) => {
    form.getTextField(`What is the hazard1`).setText(pb.description || '');
    form.getTextField(`If immediate attention is needed why is it needed1`).setText(pb.immediate_attention_reason || '');
    form.getTextField(`Street_6`).setText(pb.street1 || '');
    form.getTextField(`Street_7`).setText(pb.street2 || '');
    form.getTextField(`City_2`).setText(pb.city || '');
    form.getTextField(`State_6`).setText(pb.state || '');
    form.getTextField(`ZIP Code_6`).setText(pb.zip_code || '');

    form.getCheckBox('Check Box15#0').uncheck();
    form.getCheckBox('Check Box15#1').check();
  })

  
  let pBCount = 0;
  (data.pending_bankruptcies || []).forEach((pb,index) => {
    if (pBCount < 2) {
      form.getTextField(`PendingDebtor_${pBCount + 1}`).setText(pb.debtor || '');
      form.getTextField(`District_${pBCount + 4}`).setText(pb.district || '');
      form.getTextField(`Relationship to you_${pBCount + 1}`).setText(pb.relationship || '');
      form.getTextField(`When_${pBCount + 4}`).setText(fmtDate(pb.filing_date));
      form.getTextField(`Case number if known_${pBCount + 3}`).setText(pb.case_number || '');
    }
    pBCount++
  })
    if (pBCount > 0){
        form.getCheckBox('Check Box9#1').check();
    }else{
        form.getCheckBox('Check Box9#0').check();
    }
    if (data.pays_rent){
        form.getCheckBox('Check Box10#1').check();
        if (data.being_evicted){
            form.getCheckBox('Check Box11#1').check();
        } else {
            form.getCheckBox('Check Box11#0').check();
        }
    }else{
        form.getCheckBox('Check Box10#0').check();
        form.getCheckBox('Check Box11#0').check();
    }
    
    //Attorney defaults
    form.getTextField(`Attorney.Sig`).setText("s/ Oliver Cisowski");
    form.getTextField(`Attorney.Date signed`).setText(fmtDate(execDateISO));
    form.getTextField(`Attorney.Printed name`).setText("Oliver Cisowski");
    form.getTextField(`Attorney.Firm name`).setText("Cisowski Law");
    form.getTextField(`Attorney.Street address_2`).setText("3119 Bristol Hwy Ste 217");
    form.getTextField(`Attorney.Street address_3`).setText("");
    form.getTextField(`Attorney.City`).setText("Johnson City");
    form.getTextField(`Attorney.State`).setText("TN");
    form.getTextField(`Attorney.Zip`).setText("37601");
    form.getTextField(`Attorney.phone`).setText("(423) 342-4722");
    form.getTextField(`Attorney.Email address`).setText("oliver@cisowskilaw.com");
    form.getTextField(`Attorney.Bar number`).setText("036759");
    form.getTextField(`Attorney.Bar State`).setText("TN");

    // some defaults because i dont do biz cases
    form.getCheckBox('Check Box12#0').check();
    form.getCheckBox('Check Box14#0').check();

    // fill debtor fields
    let priorBKCount = 0;
    
  (data.debtors || []).forEach((debtor, index) => {
    (debtor.prior_bankruptcies || []).forEach((bk, index) => {
      if (priorBKCount < 3){
        form.getTextField(`District_${priorBKCount + 1}`).setText(bk.district || '');
        form.getTextField(`When_${priorBKCount + 1}`).setText(fmtDate(bk.filing_date));
        form.getTextField(`Case number_${priorBKCount + 1}`).setText(bk.case_number || '');
      }
      priorBKCount++
    })
    if (priorBKCount > 0)
    {
      form.getCheckBox('Check Box8#1').check();
    }
    else{
      form.getCheckBox('Check Box8#0').check();
    }
    const isPrimary = debtor.is_primary;
    if (isPrimary){
      form.getTextField('Debtor1.Name').setText(
        (debtor.first_name || '') + " " +
        (debtor.second_name || '') + " " +
        (debtor.last_name || '') + " " +
        (debtor.suffix || '')
      );
      form.getCheckBox('Check Box16#0').check();
    }
    else{
      form.getCheckBox('Check Box17#0').check();
    }
    const prefix = isPrimary ? 'Debtor1' : 'Debtor2';
    // SSN and ETIN last 4 digits
    form.getTextField(`${prefix}.signature`).setText(`s/ ${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
    form.getTextField(`${prefix}.Executed on`).setText(fmtDate(execDateISO));
    form.getTextField(`${prefix}.SSNum`).setText(debtor.ssn ? debtor.ssn.slice(-4) : '');
    form.getTextField(`${prefix}.Tax Payer IDNum`).setText(debtor.etin ? debtor.etin.slice(-4) : '');
    form.getTextField(`${prefix}.Employer Identification Number1`).setText(debtor.ein || '');

    // names
    form.getTextField(`${prefix}.First name`).setText(debtor.first_name || '');
    form.getTextField(`${prefix}.Middle name`).setText(debtor.second_name || '');
    form.getTextField(`${prefix}.Last name`).setText(debtor.last_name || '');
    form.getTextField(`${prefix}.Suffix Sr Jr II III`).setText(debtor.suffix || '');

    // aliases
    let nameAliasCount = 0;
    let bizAliasCount = 0;
    (debtor.aliases || []).forEach(alias => {
      const type = (alias.alias_type || '').toLowerCase();
      if (['fdba', 'pdba'].includes(type)) {
        // business alias
        const field = bizAliasCount === 0
          ? `${prefix}.Business name`
          : `${prefix}.Business name_${bizAliasCount + (isPrimary ? 3 : 4)}`;
        form.getTextField(field).setText(alias.first_name || '');
        bizAliasCount++;
      } else {
        // personal alias
        const idx = `_${nameAliasCount * 2 + (isPrimary ? 3 : 4)}`;
        form.getTextField(`${prefix}.First name${idx}`).setText(alias.first_name || '');
        form.getTextField(`${prefix}.Middle name${idx}`).setText(alias.second_name || '');
        form.getTextField(`${prefix}.Last name${idx}`).setText(alias.last_name || '');
        nameAliasCount++;
      }
    });

    // physical address: always fill for primary; for non-primary only if any physical field differs
    const physFields = ['street','street2','city','state','zip_code','county'];
    if (isPrimary || physFields.some(f => debtor[f] !== primaryDebtor[f])) {
      form.getTextField(`${prefix}.Street`).setText(debtor.street || '');
      form.getTextField(`${prefix}.Street1`).setText(debtor.street2 || '');
      form.getTextField(`${prefix}.City`).setText(debtor.city || '');
      form.getTextField(`${prefix}.State`).setText(debtor.state || '');
      form.getTextField(`${prefix}.ZIP Code`).setText(debtor.zip_code || '');
      form.getTextField(`${prefix}.County`).setText(debtor.county || '');
    }

    // mailing address: always fill for primary; for non-primary only if any mailing field differs
    const mailFields = ['mailing_street','mailing_street2','mailing_city','mailing_state','mailing_zip_code'];
    if (isPrimary || mailFields.some(f => debtor[f] !== primaryDebtor[f])) {
      form.getTextField(`${prefix}.Street_2`).setText(debtor.mailing_street || '');
      form.getTextField(`${prefix}.PO Box`).setText(debtor.mailing_street2 || '');
      form.getTextField(`${prefix}.City_2`).setText(debtor.mailing_city || '');
      form.getTextField(`${prefix}.State_2`).setText(debtor.mailing_state || '');
      form.getTextField(`${prefix}.ZIP Code_2`).setText(debtor.mailing_zip_code || '');
    }
  })

    if (data.alternative_venue_reason){
        form.getCheckBox('Check Box5#1').check();
        form.getTextField(`Debtor1.See 28 USC  1408 1`).setText(data.alternative_venue_reason || '');
        if ((data.debtors || []).length > 1) {
            form.getCheckBox('Check Box6#1').check();
        }
    }
    else{
        form.getCheckBox('Check Box5#0').check();
            // Check Box6 based on multiple debtors
        if ((data.debtors || []).length > 1) {
            form.getCheckBox('Check Box6#0').check();
        }


    }

    if (data.filing_fee_method === 'installment'){
        form.getCheckBox('Check Box7#1').check();
    }
    else if (data.filing_fee_method === 'with_petition'){
        form.getCheckBox('Check Box7#0').check();
    }
    else {
        form.getCheckBox('Check Box7#2').check();
    }
    // checkboxe
  
    // embed a standard built-in PDF font
    const [firstPage] = pdfDoc.getPages();
    const pageWidth = firstPage.getWidth();
    const pageHeight = firstPage.getHeight();
    const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
    form.updateFieldAppearances(helvetica);
    form.getFields().forEach(field => {
        if (field instanceof PDFCheckBox && !field.isChecked()) {
          field.acroField.getWidgets().forEach(widget => {
            const dict = widget.acroAnnotation?.dict || widget.dict;
            // move widget off-page by setting its Rect to an out-of-bounds box
            dict.set(
              PDFName.of('Rect'),
              pdfDoc.context.obj([  pageWidth - 20, pageHeight - 20, 10, 10 ])
            );
          });
        }
      });
      
    
    form.flatten();
  
    // Begin merging: base form always first
    const merged = await PDFDocument.create();
    const basePages = await merged.copyPages(pdfDoc, pdfDoc.getPageIndices());
    basePages.forEach(p => merged.addPage(p));

    if (data.amended && amendedPdfDoc) {
      // then amended form
      const amendedPages = await merged.copyPages(amendedPdfDoc, amendedPdfDoc.getPageIndices());
      amendedPages.forEach(p => merged.addPage(p));
      // then creditor list
      await appendAmendmentServiceList(merged, data, generateCreditorsListPdf);
      const envelopes = await generateEnvelopesPdf(amendmentServiceRecipients(data));
    }

    const mergedBytes = await merged.save();

    // Build blob and optionally optimize server-side, but always return a Blob
    const blob = new Blob([mergedBytes], { type: 'application/pdf' });
    const formData = new FormData();
    formData.append('file', blob, 'draft.pdf');

    try {
      const res = await fetch(`${API_URL2}/optimize-pdf/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });
      if (res.ok) {
        const optBlob = await res.blob();
        return optBlob || blob;
      }
      // fall back to original if optimization service fails
      return blob;
    } catch (err) {
      console.error('Optimization failed, returning original blob:', err);
      return blob;
    }
  }

export async function generateInstallmentAppPdf(data = {}, options = {}) {
    data = { ...data, debtors: filingDebtors(data.debtors) };
    console.log('fillFormBrowser called with:', { data});
    console.log("📦 generateInstallmentAppPdf got data:", data);
    console.log("🗝️ data keys:", Object.keys(data));
  // provide defaults so we can safely reference properties
    // helper to format dates as MM/DD/YYYY
    const fmtDate = (s) => {
      if (!s) return '';
      const [year, month, day] = s.split('-');
      return `${month}/${day}/${year}`;
    };
    // helper to format dates as a long string in local time (no offset)
    const fmtLongDate = (s) => {
      if (!s) return '';
      const [year, month, day] = s.split('-').map(Number);
      const d = new Date(year, month - 1, day);
      return d.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    };
    const url = '/forms/103-installment.pdf';
    let amendedPdfDoc = null;
    const existingPdfBytes = await fetch(url).then(res => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const primaryDebtor = (data.debtors || []).find(d => d.is_primary) || {};
    const form = pdfDoc.getForm();
    // Populate debtor name and signature fields
    const execDateISO = resolveExecDate(data);
    const executionDateFormatted = fmtDate(execDateISO);
    // Primary debtor
    const primary = (data.debtors || []).find(d => d.is_primary) || {};
    const name1 = [primary.first_name, primary.second_name, primary.last_name, primary.suffix]
      .filter(Boolean).join(' ');
    form.getTextField('debtor1').setText(name1);
    form.getTextField('debtor1_sig').setText(`s/ ${name1}`);
    form.getTextField('debtor1_sig_date').setText(executionDateFormatted);

    // Joint (non-primary) debtor, if any
    const joint = (data.debtors || []).find(d => !d.is_primary);
    if (joint) {
      const name2 = [joint.first_name, joint.second_name, joint.last_name, joint.suffix]
        .filter(Boolean).join(' ');
      form.getTextField('debtor2').setText(name2);
      form.getTextField('debtor2_sig').setText(`s/ ${name2}`);
      form.getTextField('debtor2_sig_date').setText(executionDateFormatted);
    }
    form.getTextField('attorney_sig').setText(`s/ Oliver Cisowski`);
      form.getTextField('attorney_sig_date').setText(executionDateFormatted);
     if (data.amended){
            form.getCheckBox('amended').check();
            const amendedUrl = '/forms/notice-of-amendment.pdf'
            const amendedExistingPdfBytes = await fetch(amendedUrl).then(res => res.arrayBuffer());
            amendedPdfDoc = await PDFDocument.load(amendedExistingPdfBytes);
            const amendedForm = amendedPdfDoc.getForm();
    
            (data.debtors || []).forEach((debtor, index) => {
                const amendPrefix = debtor.is_primary ? 'debtor1' : 'debtor2';
                amendedForm.getTextField(`${amendPrefix}`).setText(`${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
            });
            amendedForm.getTextField('case_number').setText(data.case_number || '');
            amendedForm.getTextField('chapter').setText(data.chapter || '');
            amendedForm.getTextField('date').setText(fmtLongDate(execDateISO));
            amendedForm.getTextField('amended_files').setText("Amended Form 103A");
            amendedForm.getTextField('items_served').setText("Amended Form 103A and Notice of Amendment");
            amendedForm.getTextField('reason_amended').setText(data.amendedReason);
            amendedForm.getTextField('creditors_served_description').setText(
              amendmentServiceDescription(data),
            );
            const helvetica = await amendedPdfDoc.embedFont(StandardFonts.Helvetica);
            amendedForm.updateFieldAppearances(helvetica);
            amendedForm.flatten();
        }
    form.getTextField('district').setText('Eastern District of Tennessee');
    form.getTextField('district').setText('Eastern District of Tennessee');
    form.getTextField('case_number').setText(data.case_number || '');
    // DEBUG: list all PDF field names to verify correct mapping
    console.log("PDF installment form fields:", form.getFields().map(f => f.getName()));
    // Determine filing fee amount, with fallback to consultation fees
    const feeAmount = data.filing_fee_amount;
    console.log('DEBUG: using feeAmount =', feeAmount);
    form.getTextField('attorney_sig').setText(`s/ Oliver Cisowski`);
    form.getCheckBox('on_or_before').check();
    form.getTextField('installment_1').setText(String(feeAmount));
    form.getTextField('fee_total').setText(String(feeAmount));
    // Set installment date = execution date + 120 days
    if (execDateISO) {
      const baseDate = new Date(execDateISO);
      baseDate.setDate(baseDate.getDate() + 120);
      const [y, m, d] = baseDate.toISOString().split('T')[0].split('-');
      const installmentDateStr = `${m}/${d}/${y}`; // MM/DD/YYYY
      form.getTextField('installment_1_date').setText(installmentDateStr);
    }
    // Set chapter checkbox
    const chapter = data.chapter;
    if (chapter === "7") {
      form.getCheckBox('chapter_7').check();
    } else if (chapter === "11") {
      form.getCheckBox('chapter_11').check();
    } else if (chapter === "12") {
      form.getCheckBox('chapter_12').check();
    } else {
      form.getCheckBox('chapter_13').check();
    }

      const [firstPage] = pdfDoc.getPages();
        const pageWidth = firstPage.getWidth();
        const pageHeight = firstPage.getHeight();
        const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
        form.updateFieldAppearances(helvetica);
        form.getFields().forEach(field => {
            if (field instanceof PDFCheckBox && !field.isChecked()) {
              field.acroField.getWidgets().forEach(widget => {
                const dict = widget.acroAnnotation?.dict || widget.dict;
                // move widget off-page by setting its Rect to an out-of-bounds box
                dict.set(
                  PDFName.of('Rect'),
                  pdfDoc.context.obj([  pageWidth - 20, pageHeight - 20, 10, 10 ])
                );
              });
            }
          });
          
        
        form.flatten();
      
        // Begin merging: base form always first
        const merged = await PDFDocument.create();
        const basePages = await merged.copyPages(pdfDoc, pdfDoc.getPageIndices());
        basePages.forEach(p => merged.addPage(p));
    
        if (data.amended && amendedPdfDoc) {
          // then amended form
          const amendedPages = await merged.copyPages(amendedPdfDoc, amendedPdfDoc.getPageIndices());
          amendedPages.forEach(p => merged.addPage(p));
          // then creditor list
          await appendAmendmentServiceList(merged, data, generateCreditorsListPdf);
          const envelopes = await generateEnvelopesPdf(amendmentServiceRecipients(data));
        }
    
        // Optimize the merged PDF by enabling object streams and compression
       const mergedBytes = await merged.save();

        const blob = new Blob([mergedBytes], { type: 'application/pdf' });
        return finishGeneratedPdf(
          blob,
          '103a Application to Have Filing Fee Paid in Installments.pdf',
          options,
        );
}

export async function generateSSNVerificationPdf(data = {}, options = {}) {
  data = { ...data, debtors: filingDebtors(data.debtors) };
  console.log("generateSSNVerificationPdf called with:", data);
  console.log('fillFormBrowser called with:', { data});
    console.log("📦 generateInstallmentAppPdf got data:", data);
    console.log("🗝️ data keys:", Object.keys(data));
    // provide defaults so we can safely reference properties
    // helper to format dates as MM/DD/YYYY
    const fmtDate = (s) => {
        if (!s) return '';
        const [year, month, day] = s.split('-');
        return `${month}/${day}/${year}`;
    };
    // helper to format dates as a long string in local time (no offset)
    const fmtLongDate = (s) => {
      if (!s) return '';
      const [year, month, day] = s.split('-').map(Number);
      // month is zero-based in JS Date
      const d = new Date(year, month - 1, day);
      return d.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    };
    const url = '/forms/121-ssn.pdf';
    let amendedPdfDoc = null;
    const existingPdfBytes = await fetch(url).then(res => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const primaryDebtor = (data.debtors || []).find(d => d.is_primary) || {};
    const form = pdfDoc.getForm();
    // Populate debtor name and signature fields
    const execDateISO = resolveExecDate(data);
    const executionDateFormatted = fmtDate(execDateISO);
    // Primary debtor
    const primary = (data.debtors || []).find(d => d.is_primary) || {};
   (data.debtors || []).forEach((debtor, index) => {
        const isPrimary = debtor.is_primary;
        const prefix = isPrimary ? 'Debtor1' : 'Debtor2';
    // SSN and ETIN last 4 digits
    form.getTextField(`${prefix}.signature`).setText(`s/ ${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
    form.getTextField(`${prefix}.Executed on`).setText(fmtDate(execDateISO));
    // SSN or ITIN handling: if missing, check the box; otherwise fill the number
    if (debtor.ssn) {
      form.getTextField(`${prefix}.SSNum`).setText(debtor.ssn);
    } else {
      form.getCheckBox(`${prefix}.SSN_box`).check();
    }
    if (debtor.etin) {
      form.getTextField(`${prefix}.ITINNum`).setText(debtor.etin);
    } else {
      form.getCheckBox(`${prefix}.ITIN_box`).check();
    }

    // names
    form.getTextField(`${prefix}.First name`).setText(debtor.first_name || '');
    form.getTextField(`${prefix}.Middle name`).setText(debtor.second_name || '');
    form.getTextField(`${prefix}.Last name`).setText(debtor.last_name || '');
   })
        if (data.amended){
            form.getCheckBox('amended').check();
            const amendedUrl = '/forms/notice-of-amendment.pdf'
            const amendedExistingPdfBytes = await fetch(amendedUrl).then(res => res.arrayBuffer());
            amendedPdfDoc = await PDFDocument.load(amendedExistingPdfBytes);
            const amendedForm = amendedPdfDoc.getForm();

            (data.debtors || []).forEach((debtor, index) => {
                const amendPrefix = debtor.is_primary ? 'debtor1' : 'debtor2';
                amendedForm.getTextField(`${amendPrefix}`).setText(`${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
            });
            amendedForm.getTextField('case_number').setText(data.case_number || '');
            amendedForm.getTextField('chapter').setText(data.chapter || '');
            amendedForm.getTextField('date').setText(fmtLongDate(execDateISO));
            amendedForm.getTextField('amended_files').setText("Amended Form 121");
            amendedForm.getTextField('items_served').setText("Amended Form 121 and Notice of Amendment");
            amendedForm.getTextField('reason_amended').setText(data.amendedReason);
            amendedForm.getTextField('creditors_served_description').setText(
              amendmentServiceDescription(data),
            );
            const helvetica = await amendedPdfDoc.embedFont(StandardFonts.Helvetica);
            amendedForm.updateFieldAppearances(helvetica);
            amendedForm.flatten();
        }
    form.getTextField('district').setText('Eastern District of Tennessee');
    form.getTextField('case_number').setText(data.case_number || '');
    // DEBUG: list all PDF field names to verify correct mapping
    console.log("PDF installment form fields:", form.getFields().map(f => f.getName()));
    // Determine filing fee amount, with fallback to consultation fees
    const feeAmount = data.filing_fee_amount;
    console.log('DEBUG: using feeAmount =', feeAmount);


    // Set chapter checkbox


        const [firstPage] = pdfDoc.getPages();
        const pageWidth = firstPage.getWidth();
        const pageHeight = firstPage.getHeight();
        const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
        form.updateFieldAppearances(helvetica);
        form.getFields().forEach(field => {
            if (field instanceof PDFCheckBox && !field.isChecked()) {
                field.acroField.getWidgets().forEach(widget => {
                const dict = widget.acroAnnotation?.dict || widget.dict;
                // move widget off-page by setting its Rect to an out-of-bounds box
                dict.set(
                    PDFName.of('Rect'),
                    pdfDoc.context.obj([  pageWidth - 20, pageHeight - 20, 10, 10 ])
                );
                });
            }
            });
            
        
        form.flatten();
        
        // Begin merging: base form always first
        const merged = await PDFDocument.create();
        const basePages = await merged.copyPages(pdfDoc, pdfDoc.getPageIndices());
        basePages.forEach(p => merged.addPage(p));

        if (data.amended && amendedPdfDoc) {
            // then amended form
            const amendedPages = await merged.copyPages(amendedPdfDoc, amendedPdfDoc.getPageIndices());
            amendedPages.forEach(p => merged.addPage(p));
            // then creditor list
            await appendAmendmentServiceList(merged, data, generateCreditorsListPdf);
            const envelopes = await generateEnvelopesPdf(amendmentServiceRecipients(data));
        }

               const mergedBytes = await merged.save();

        const blob = new Blob([mergedBytes], { type: 'application/pdf' });
        return finishGeneratedPdf(blob, '121 SSN Statement.pdf', options);
}

export async function generatePaymentAdvicesPdf(data = {}, options = {}) {
  data = { ...data, debtors: filingDebtors(data.debtors) };
console.log("generateSSNVerificationPdf called with:", data);
  console.log('fillFormBrowser called with:', { data});
    console.log("📦 generateInstallmentAppPdf got data:", data);
    console.log("🗝️ data keys:", Object.keys(data));
    // provide defaults so we can safely reference properties
    // helper to format dates as MM/DD/YYYY
    const fmtDate = (s) => {
        if (!s) return '';
        const [year, month, day] = s.split('-');
        return `${month}/${day}/${year}`;
    };
    // helper to format dates as a long string in local time (no offset)
    const fmtLongDate = (s) => {
        if (!s) return '';
        const [year, month, day] = s.split('-').map(Number);
        const d = new Date(year, month - 1, day);
        return d.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        });
    };
    const url = '/forms/1007-pay-advices.pdf';
    let amendedPdfDoc = null;
    const existingPdfBytes = await fetch(url).then(res => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const primaryDebtor = (data.debtors || []).find(d => d.is_primary) || {};
    const form = pdfDoc.getForm();
    // Populate debtor name and signature fields
    const execDateISO = resolveExecDate(data);
    const executionDateFormatted = fmtDate(execDateISO);
    form.getTextField(`chapter`).setText(data.chapter);
    // Primary debtor
    const primary = (data.debtors || []).find(d => d.is_primary) || {};
   (data.debtors || []).forEach((debtor, index) => {
        const isPrimary = debtor.is_primary;
        const prefix = isPrimary ? 'Debtor1' : 'Debtor2';
        // Payment advices are controlled per employment. Keep the existing
        // recent-employment window so older jobs do not affect this form.
        const ninetyDaysAgo = new Date();
        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
        const hasPaymentAdvices = Array.isArray(debtor.employments) && debtor.employments.some(emp => {
          // The saved employment choice is authoritative. A missing value must
          // not cause the form to certify that payment advices exist.
          const employmentHasAdvices = [true, 'true', 1, '1', 'yes'].includes(
            emp.has_payment_advices
          );
          if (!employmentHasAdvices) return false;
          if (!emp.end_date) return true;
          const endDate = new Date(emp.end_date);
          return endDate >= ninetyDaysAgo;
        });
        if (hasPaymentAdvices) {
          form.getCheckBox(`${prefix}_yes`).check();
        } else {
          form.getCheckBox(`${prefix}_no`).check();
        }
    // SSN and ETIN last 4 digits
    form.getTextField(`${prefix}_signature`).setText(`s/ ${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
    form.getTextField(`${prefix}`).setText(`${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
    form.getTextField(`${prefix}_date`).setText(fmtDate(execDateISO));

   })
        if (data.amended){
            form.getCheckBox('amended').check();
            const amendedUrl = '/forms/notice-of-amendment.pdf'
            const amendedExistingPdfBytes = await fetch(amendedUrl).then(res => res.arrayBuffer());
            amendedPdfDoc = await PDFDocument.load(amendedExistingPdfBytes);
            const amendedForm = amendedPdfDoc.getForm();

            (data.debtors || []).forEach((debtor, index) => {
                const amendPrefix = debtor.is_primary ? 'debtor1' : 'debtor2';
                amendedForm.getTextField(`${amendPrefix}`).setText(`${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
            });
            amendedForm.getTextField('case_number').setText(data.case_number || '');
            amendedForm.getTextField('chapter').setText(data.chapter || '');
            amendedForm.getTextField('date').setText(fmtLongDate(execDateISO));
            amendedForm.getTextField('amended_files').setText("Amended Local Form 1007.1");
            amendedForm.getTextField('items_served').setText("Amended Form 1007.1 and Notice of Amendment");
            amendedForm.getTextField('reason_amended').setText(data.amendedReason);
            amendedForm.getTextField('creditors_served_description').setText(
              amendmentServiceDescription(data),
            );
            const helvetica = await amendedPdfDoc.embedFont(StandardFonts.Helvetica);
            amendedForm.updateFieldAppearances(helvetica);
            amendedForm.flatten();
        }
    form.getTextField('case_number').setText(data.case_number || '');
    // DEBUG: list all PDF field names to verify correct mapping
    console.log("PDF installment form fields:", form.getFields().map(f => f.getName()));
    // Determine filing fee amount, with fallback to consultation fees
    const feeAmount = data.filing_fee_amount;
    console.log('DEBUG: using feeAmount =', feeAmount);


    // Set chapter checkbox


        const [firstPage] = pdfDoc.getPages();
        const pageWidth = firstPage.getWidth();
        const pageHeight = firstPage.getHeight();
        const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
        form.updateFieldAppearances(helvetica);
        form.getFields().forEach(field => {
            if (field instanceof PDFCheckBox && !field.isChecked()) {
                field.acroField.getWidgets().forEach(widget => {
                const dict = widget.acroAnnotation?.dict || widget.dict;
                // move widget off-page by setting its Rect to an out-of-bounds box
                dict.set(
                    PDFName.of('Rect'),
                    pdfDoc.context.obj([  pageWidth - 20, pageHeight - 20, 10, 10 ])
                );
                });
            }
            });
            
        
        form.flatten();
        
        // Begin merging: base form always first
        const merged = await PDFDocument.create();
        const basePages = await merged.copyPages(pdfDoc, pdfDoc.getPageIndices());
        basePages.forEach(p => merged.addPage(p));

        if (data.amended && amendedPdfDoc) {
            // then amended form
            const amendedPages = await merged.copyPages(amendedPdfDoc, amendedPdfDoc.getPageIndices());
            amendedPages.forEach(p => merged.addPage(p));
            // then creditor list
            await appendAmendmentServiceList(merged, data, generateCreditorsListPdf);
            const envelopes = await generateEnvelopesPdf(amendmentServiceRecipients(data));
        }

       const mergedBytes = await merged.save();

        const blob = new Blob([mergedBytes], { type: 'application/pdf' });
        return finishGeneratedPdf(blob, '1007 Payment Advices.pdf', options);
        }
export async function generateAttorneyCompensationPdf(data = {}, options = {}) {
  data = { ...data, debtors: filingDebtors(data.debtors) };
  console.log("generateAttorneyCompensationPdf called with:", data);
  console.log('fillFormBrowser called with:', { data});
    console.log("📦 generateInstallmentAppPdf got data:", data);
    console.log("🗝️ data keys:", Object.keys(data));
    // provide defaults so we can safely reference properties
    // helper to format dates as MM/DD/YYYY
    const fmtDate = (s) => {
        if (!s) return '';
        const [year, month, day] = s.split('-');
        return `${month}/${day}/${year}`;
    };
    // helper to format dates as a long string in local time (no offset)
    const fmtLongDate = (s) => {
      if (!s) return '';
      const [year, month, day] = s.split('-').map(Number);
      const d = new Date(year, month - 1, day);
      return d.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    };
    const url = '/forms/2030-attorney-disclosure-form.pdf';
    let amendedPdfDoc = null;
    const existingPdfBytes = await fetch(url).then(res => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const primaryDebtor = (data.debtors || []).find(d => d.is_primary) || {};
    const form = pdfDoc.getForm();
    // Populate debtor name and signature fields
    const execDateISO = resolveExecDate(data);
    const executionDateFormatted = fmtDate(execDateISO);
    form.getTextField(`chapter`).setText(data.chapter);
    form.getTextField(`date`).setText(executionDateFormatted);
    // Calculate attorney fees, paid amounts, and balance
    const totalFees = data.total_attorney_fees ?? 0;
    const paidFees = data.fees_paid_prior ?? 0;
    const balanceAmt = totalFees - paidFees;
    form.getTextField('total_attorney_fees').setText(String(totalFees));
    form.getTextField('fees_paid_prior').setText(String(paidFees));
    form.getTextField('balance').setText(String(balanceAmt));
    if (data.fees_source === 'debtor'){
      form.getCheckBox('compensation_source_debtor').check();
    }
    else {
      form.getCheckBox('compensation_source_other').check();
      form.getTextField(`compensation_source_description`).setText(data.fees_source_description);
    }
    if (balanceAmt > 0) {
      if (data.fees_to_be_paid_source === 'debtor'){
        form.getCheckBox('compensation_to_be_debtor').check();
      }
      else {
        form.getCheckBox('compensation_to_be_other').check();
        form.getTextField(`compensation_to_be_description`).setText(data.fees_to_be_paid_description);
      }
    }

    if (data.share_fees){
      form.getCheckBox('share_fee_yes').check();
    }
    else {
      form.getCheckBox('share_fee_no').check();
    }
    form.getTextField('attorney_signature').setText(`s/ Oliver Cisowski`);
    // Primary debtor
    const primary = (data.debtors || []).find(d => d.is_primary) || {};
   (data.debtors || []).forEach((debtor, index) => {
        const isPrimary = debtor.is_primary;
        const prefix = isPrimary ? 'debtor1' : 'debtor2';

    form.getTextField(`${prefix}`).setText(`${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);


   })
        if (data.amended){
            form.getCheckBox('amended').check();
            const amendedUrl = '/forms/notice-of-amendment.pdf'
            const amendedExistingPdfBytes = await fetch(amendedUrl).then(res => res.arrayBuffer());
            amendedPdfDoc = await PDFDocument.load(amendedExistingPdfBytes);
            const amendedForm = amendedPdfDoc.getForm();

            (data.debtors || []).forEach((debtor, index) => {
                const amendPrefix = debtor.is_primary ? 'debtor1' : 'debtor2';
                amendedForm.getTextField(`${amendPrefix}`).setText(`${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
            });
            amendedForm.getTextField('case_number').setText(data.case_number || '');
            amendedForm.getTextField('chapter').setText(data.chapter || '');
            amendedForm.getTextField('date').setText(fmtLongDate(execDateISO));
            amendedForm.getTextField('amended_files').setText("Amended Form 2030");
            amendedForm.getTextField('items_served').setText("Amended Form 2030 and Notice of Amendment");
            amendedForm.getTextField('reason_amended').setText(data.amendedReason);
            amendedForm.getTextField('creditors_served_description').setText(
              amendmentServiceDescription(data),
            );
            const helvetica = await amendedPdfDoc.embedFont(StandardFonts.Helvetica);
            amendedForm.updateFieldAppearances(helvetica);
            amendedForm.flatten();
        }
    form.getTextField('case_number').setText(data.case_number || '');
    form.getTextField('district').setText('Eastern District of Tennessee');
    // DEBUG: list all PDF field names to verify correct mapping
    console.log("PDF installment form fields:", form.getFields().map(f => f.getName()));
    // Determine filing fee amount, with fallback to consultation fees
    const feeAmount = data.filing_fee_amount;
    console.log('DEBUG: using feeAmount =', feeAmount);


    // Set chapter checkbox


        const [firstPage] = pdfDoc.getPages();
        const pageWidth = firstPage.getWidth();
        const pageHeight = firstPage.getHeight();
        const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
        form.updateFieldAppearances(helvetica);
        form.getFields().forEach(field => {
            if (field instanceof PDFCheckBox && !field.isChecked()) {
                field.acroField.getWidgets().forEach(widget => {
                const dict = widget.acroAnnotation?.dict || widget.dict;
                // move widget off-page by setting its Rect to an out-of-bounds box
                dict.set(
                    PDFName.of('Rect'),
                    pdfDoc.context.obj([  pageWidth - 20, pageHeight - 20, 10, 10 ])
                );
                });
            }
            });
            
        
        form.flatten();
        
        // Begin merging: base form always first
        const merged = await PDFDocument.create();
        const basePages = await merged.copyPages(pdfDoc, pdfDoc.getPageIndices());
        basePages.forEach(p => merged.addPage(p));

        if (data.amended && amendedPdfDoc) {
            // then amended form
            const amendedPages = await merged.copyPages(amendedPdfDoc, amendedPdfDoc.getPageIndices());
            amendedPages.forEach(p => merged.addPage(p));
            // then creditor list
            await appendAmendmentServiceList(merged, data, generateCreditorsListPdf);
        }

        const mergedBytes = await merged.save();

        const blob = new Blob([mergedBytes], { type: 'application/pdf' });
        return finishGeneratedPdf(blob, '2030 Disclosure of Atty Compensation.pdf', options);
}

export async function generateDEBNFormsPdf(data = {}, isPrimary, options = {}) {
  data = { ...data, debtors: filingDebtors(data.debtors) };
  console.log("generateDEBNFormsPdf called with debtors:", data);
  console.log('fillFormBrowser called with:', { data});
    console.log("📦 generateInstallmentAppPdf got data:", data);
    console.log("🗝️ data keys:", Object.keys(data));
    // provide defaults so we can safely reference properties
    // helper to format dates as MM/DD/YYYY
    const fmtDate = (s) => {
        if (!s) return '';
        const [year, month, day] = s.split('-');
        return `${month}/${day}/${year}`;
    };
    // helper to format dates as a long string in local time (no offset)
    const fmtLongDate = (s) => {
        if (!s) return '';
        const [year, month, day] = s.split('-').map(Number);
        const d = new Date(year, month - 1, day);
        return d.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        });
    };
    const url = '/forms/9036-debn.pdf';
    let amendedPdfDoc = null;
    const existingPdfBytes = await fetch(url).then(res => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const primaryDebtor = (data.debtors || []).find(d => d.is_primary) || {};
    const form = pdfDoc.getForm();
    // Populate debtor name and signature fields
    const execDateISO = resolveExecDate(data);
    const executionDateFormatted = fmtDate(execDateISO);

    form.getTextField(`date`).setText(executionDateFormatted);
    
    // Primary debtor
    if (isPrimary)
    {
      (data.debtors || []).forEach((debtor, index) => {
      if (debtor.is_primary === true) {
       form.getTextField(`debtor`).setText(`${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`); 
       form.getTextField(`debtor_signature`).setText(`s/ ${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
       if (debtor.debn_notice){
        form.getCheckBox('initial_request').check();
        form.getTextField(`email`).setText(`${(debtor.email)}`); 
       }
       else {
        form.getCheckBox('initial_decline').check();
       }
      }
    })
    }
    else {
            (data.debtors || []).forEach((debtor, index) => {
      if (debtor.is_primary !== true) {
       form.getTextField(`debtor`).setText(`${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`); 
       form.getTextField(`debtor_signature`).setText(`s/ ${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
       if (debtor.debn_notice){
        form.getCheckBox('initial_request').check();
        form.getTextField(`email`).setText(`${(debtor.email)}`); 
       }
       else {
        form.getCheckBox('initial_decline').check();
       }
      }
    })
    }

        if (data.amended){
            form.getCheckBox('amended').check();
            const amendedUrl = '/forms/notice-of-amendment.pdf'
            const amendedExistingPdfBytes = await fetch(amendedUrl).then(res => res.arrayBuffer());
            amendedPdfDoc = await PDFDocument.load(amendedExistingPdfBytes);
            const amendedForm = amendedPdfDoc.getForm();

            (data.debtors || []).forEach((debtor, index) => {
                const amendPrefix = debtor.is_primary ? 'debtor1' : 'debtor2';
                amendedForm.getTextField(`${amendPrefix}`).setText(`${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
            });
            amendedForm.getTextField('case_number').setText(data.case_number || '');
            amendedForm.getTextField('chapter').setText(data.chapter || '');
            amendedForm.getTextField('date').setText(fmtLongDate(execDateISO));
            amendedForm.getTextField('amended_files').setText("Amended Form 9036");
            amendedForm.getTextField('items_served').setText("Amended Form 9036 and Notice of Amendment");
            amendedForm.getTextField('reason_amended').setText(data.amendedReason);
            amendedForm.getTextField('creditors_served_description').setText(
              amendmentServiceDescription(data),
            );
            const helvetica = await amendedPdfDoc.embedFont(StandardFonts.Helvetica);
            amendedForm.updateFieldAppearances(helvetica);
            amendedForm.flatten();
        }
    form.getTextField('case_number').setText(data.case_number || '');

    // DEBUG: list all PDF field names to verify correct mapping
    console.log("PDF installment form fields:", form.getFields().map(f => f.getName()));
    // Determine filing fee amount, with fallback to consultation fees



    // Set chapter checkbox


        const [firstPage] = pdfDoc.getPages();
        const pageWidth = firstPage.getWidth();
        const pageHeight = firstPage.getHeight();
        const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
        form.updateFieldAppearances(helvetica);
        form.getFields().forEach(field => {
            if (field instanceof PDFCheckBox && !field.isChecked()) {
                field.acroField.getWidgets().forEach(widget => {
                const dict = widget.acroAnnotation?.dict || widget.dict;
                // move widget off-page by setting its Rect to an out-of-bounds box
                dict.set(
                    PDFName.of('Rect'),
                    pdfDoc.context.obj([  pageWidth - 20, pageHeight - 20, 10, 10 ])
                );
                });
            }
            });
            
        
        form.flatten();
        
        // Begin merging: base form always first
        const merged = await PDFDocument.create();
        const basePages = await merged.copyPages(pdfDoc, pdfDoc.getPageIndices());
        basePages.forEach(p => merged.addPage(p));

        if (data.amended && amendedPdfDoc) {
            // then amended form
            const amendedPages = await merged.copyPages(amendedPdfDoc, amendedPdfDoc.getPageIndices());
            amendedPages.forEach(p => merged.addPage(p));
            // then creditor list
            await appendAmendmentServiceList(merged, data, generateCreditorsListPdf);
            const envelopes = await generateEnvelopesPdf(amendmentServiceRecipients(data));
        }
        const mergedBytes = await merged.save();

        const blob = new Blob([mergedBytes], { type: 'application/pdf' });
        const fileName = isPrimary ? '9036 DEBN D1.pdf' : '9036 DEBN D2.pdf';
        return finishGeneratedPdf(blob, fileName, options);
}
